import { useCallback, useEffect, useState } from 'react'
import {
  adminActAs,
  adminCreateProspect,
  adminListProspects,
  adminSendClaimLink,
  type AdminProspectRow,
} from '../../lib/admin'
import { useAuth } from '../../lib/auth'

/**
 * Accounts built for people who don't have one yet.
 *
 * This page is deliberately thin. It creates the container, hands you the keys
 * to it, and emails the claim link — and nothing else, because everything else
 * already exists in the app. "Build in this account" drops you into the real
 * Encore, scoped to the prospect, where the real tour flow, the real CSV
 * import and the real backfill do the work.
 *
 * The alternative was a second set of creation forms living here, drifting
 * from the ones customers use, and a pitch given from a surface that is not
 * the product.
 */
export default function AdminProspects() {
  const { refreshClaims } = useAuth()

  const [rows, setRows] = useState<AdminProspectRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const [name, setName] = useState('')
  const [intendedEmail, setIntendedEmail] = useState('')
  const [note, setNote] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const result = await adminListProspects({})
      setRows(result.prospects)
      setError(null)
    } catch (caught) {
      setError((caught as Error).message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const create = async () => {
    if (!name.trim()) return
    setBusy('create')
    try {
      await adminCreateProspect({
        name: name.trim(),
        intendedEmail: intendedEmail.trim() || undefined,
        note: note.trim() || undefined,
      })
      setName('')
      setIntendedEmail('')
      setNote('')
      await load()
    } catch (caught) {
      setError((caught as Error).message)
    } finally {
      setBusy(null)
    }
  }

  const build = async (row: AdminProspectRow) => {
    setBusy(row.orgId)
    try {
      const grant = await adminActAs({ orgId: row.orgId })
      sessionStorage.setItem('encore.actingAs', grant.orgName)
      // The claims are set server-side; the cached token still says otherwise
      // until it is forced to refetch, and the rules read the token.
      await refreshClaims()
      window.location.assign('/app')
    } catch (caught) {
      setError((caught as Error).message)
      setBusy(null)
    }
  }

  const sendClaim = async (row: AdminProspectRow) => {
    const email = window.prompt(
      `Send a claim link for ${row.name} to:`,
      row.intendedEmail ?? '',
    )
    if (!email) return

    setBusy(row.orgId)
    try {
      await adminSendClaimLink({ orgId: row.orgId, email: email.trim() })
      await load()
    } catch (caught) {
      setError((caught as Error).message)
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="stack" style={{ gap: 20, padding: 24, maxWidth: 940 }}>
      <div className="stack" style={{ gap: 4 }}>
        <span className="t-eyebrow">Prospects</span>
        <h1 className="t-display-s">Accounts you've built</h1>
        <p className="t-caption" style={{ maxWidth: 620 }}>
          An account with no login behind it. Build it out, show it to them, then
          email a claim link — nothing exists against their address until they
          open it.
        </p>
      </div>

      {error && (
        <div className="panel panel-pad" style={{ borderColor: 'var(--danger)' }}>
          <p className="t-caption" style={{ color: 'var(--danger)' }}>{error}</p>
        </div>
      )}

      <div className="panel panel-pad stack" style={{ gap: 12 }}>
        <span className="t-eyebrow">New account</span>
        <div style={{ display: 'grid', gap: 10, gridTemplateColumns: '1fr 1fr' }}>
          <label className="stack" style={{ gap: 4 }}>
            <span className="t-spec-caption faint">NAME</span>
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Cult Artists"
            />
          </label>
          <label className="stack" style={{ gap: 4 }}>
            <span className="t-spec-caption faint">WHO IT'S FOR (OPTIONAL)</span>
            <input
              value={intendedEmail}
              onChange={(event) => setIntendedEmail(event.target.value)}
              placeholder="name@label.com"
              type="email"
            />
          </label>
        </div>
        <label className="stack" style={{ gap: 4 }}>
          <span className="t-spec-caption faint">NOTE TO SELF (OPTIONAL)</span>
          <input
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Met at BIGSOUND — wants the agency view"
          />
        </label>
        <button
          className="btn btn-primary"
          onClick={create}
          disabled={!name.trim() || busy === 'create'}
          style={{ alignSelf: 'flex-start' }}
        >
          {busy === 'create' ? 'Creating…' : 'Create account'}
        </button>
      </div>

      {loading ? (
        <p className="t-caption faint">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="t-caption faint">Nothing built yet.</p>
      ) : (
        <div className="stack" style={{ gap: 10 }}>
          {rows.map((row) => (
            <div
              key={row.orgId}
              className="panel panel-pad"
              style={{ display: 'flex', alignItems: 'center', gap: 16 }}
            >
              <div className="stack" style={{ gap: 3, minWidth: 0, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <strong>{row.name}</strong>
                  {row.claimedAt && (
                    <span className="t-spec-caption" style={{ color: 'var(--accent)' }}>
                      CLAIMED
                    </span>
                  )}
                </div>
                <span className="t-caption faint">
                  {row.artistCount} artist{row.artistCount === 1 ? '' : 's'} ·{' '}
                  {row.tourCount} tour{row.tourCount === 1 ? '' : 's'}
                  {row.intendedEmail ? ` · ${row.intendedEmail}` : ''}
                </span>
                {row.note && <span className="t-spec-caption faint">{row.note}</span>}
              </div>

              <button
                className="btn btn-secondary"
                onClick={() => sendClaim(row)}
                disabled={busy === row.orgId || Boolean(row.claimedAt)}
              >
                Send claim link
              </button>
              <button
                className="btn btn-primary"
                onClick={() => build(row)}
                disabled={busy === row.orgId}
              >
                {busy === row.orgId ? 'Opening…' : 'Build in this account'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
